import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

import { StackableModal, FilterDropdown } from '../components/nextgen';
import Button from '../components/Button';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import { getContracts } from '../api/contracts';
import { StatusBadge } from '../components/nextgen/Badge';

const MODAL_NAME = 'contractPicker';

const ContractPickerModal = () => {
	const { open: options, setClose } = useModal(MODAL_NAME);
	const [selectedContracts, setSelectedContracts] = useState([]);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');

	const { value: contracts, execute: fetchContracts, status } = useAsync(
		getContracts,
		{ immediate: false }
	);

	useEffect(() => {
		if (options) {
			fetchContracts({});
			setSelectedContracts([]);
			setSearch('');
			setStatusFilter('all');
		}
	}, [options, fetchContracts]);

	const handleSelect = (contract) => {
		if (options?.multiSelect) {
			if (selectedContracts.find((c) => c.id === contract.id)) {
				setSelectedContracts(selectedContracts.filter((c) => c.id !== contract.id));
			} else {
				const maxSelect = options?.maxSelect || Infinity;
				if (selectedContracts.length < maxSelect) {
					setSelectedContracts([...selectedContracts, contract]);
				}
			}
		} else {
			setSelectedContracts([contract]);
		}
	};

	const handleConfirm = () => {
		if (options?.onSelect && selectedContracts.length > 0) {
			options.onSelect(selectedContracts);
		}
		setClose();
	};

	const getContractStatus = (contract) => {
		if (!contract?.endDate) {
			return { label: 'Active', color: 'green' };
		}

		const now = moment();
		const endDate = moment(contract.endDate);

		if (!endDate.isValid()) {
			return { label: 'Active', color: 'green' };
		}

		const isExpired = now.isAfter(endDate);
		const daysUntilExpiry = endDate.diff(now, 'days');
		const isExpiringSoon = !isExpired && daysUntilExpiry <= 30 && daysUntilExpiry >= 0;

		if (isExpired) {
			return { label: 'Expired', color: 'red' };
		} else if (isExpiringSoon) {
			return { label: `Expiring in ${daysUntilExpiry}d`, color: 'orange' };
		} else {
			return { label: 'Active', color: 'green' };
		}
	};

	const filteredContracts = contracts?.rows?.filter((contract) => {
		// Search filter
		if (search) {
			const searchLower = search.toLowerCase();
			const matchesSearch =
				contract?.name?.toLowerCase().includes(searchLower) ||
				contract?.vendor?.companyName?.toLowerCase().includes(searchLower) ||
				contract?.contractNumber?.toLowerCase().includes(searchLower);
			if (!matchesSearch) return false;
		}

		// Status filter
		if (statusFilter !== 'all') {
			const statusInfo = getContractStatus(contract);
			const contractStatus = statusInfo.label.toLowerCase();

			if (statusFilter === 'active' && !contractStatus.includes('active')) {
				return false;
			}
			if (statusFilter === 'expired' && contractStatus !== 'expired') {
				return false;
			}
			if (statusFilter === 'expiring' && !contractStatus.includes('expiring')) {
				return false;
			}
		}

		return true;
	}) || [];

	// Filter out contracts that are already attached to the asset
	const skip = options?.skip || [];
	const availableContracts = filteredContracts.filter(
		(contract) => !skip.includes(contract.id)
	);

	const isLoading = status === 'pending';

	return (
		<StackableModal
			open={!!options}
			onClose={setClose}
			title={options?.title || 'Select Contracts'}
			size="2xl"
		>
			<div className="space-y-4">
				{/* Search and Filters */}
				<div className="flex gap-3">
					<input
						type="text"
						placeholder="Search contracts..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
					/>
					<FilterDropdown
					label="Status"
					placeholder="All Statuses"
					value={statusFilter}
					options={[
						{ value: 'all', label: 'All Statuses' },
						{ value: 'active', label: 'Active' },
						{ value: 'expiring', label: 'Expiring Soon' },
						{ value: 'expired', label: 'Expired' },
					]}
					onChange={setStatusFilter}
				/>
				</div>

				{/* Contract List */}
				<div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
					{isLoading ? (
						<div className="p-8 text-center text-gray-500">
							Loading contracts...
						</div>
					) : availableContracts.length === 0 ? (
						<div className="p-8 text-center text-gray-500">
							No contracts found
						</div>
					) : (
						<ul className="divide-y divide-gray-200">
							{availableContracts.map((contract) => {
								const isSelected = selectedContracts.find(
									(c) => c.id === contract.id
								);
								const statusInfo = getContractStatus(contract);

								return (
									<li
										key={contract.id}
										onClick={() => handleSelect(contract)}
										className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
											isSelected ? 'bg-indigo-50' : ''
										}`}
									>
										<div className="flex items-start justify-between">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<p className="text-sm font-medium text-gray-900 truncate">
														{contract?.name || 'Untitled Contract'}
													</p>
													<StatusBadge color={statusInfo.color}>
														{statusInfo.label}
													</StatusBadge>
												</div>
												<p className="text-xs text-gray-500">
													{contract?.vendor?.companyName && (
														<span className="mr-3">
															Vendor: {contract.vendor.companyName}
														</span>
													)}
													{contract?.contractNumber && (
														<span className="mr-3">
															#{contract.contractNumber}
														</span>
													)}
													{contract?.endDate && (
														<span>
															Expires: {moment(contract.endDate).format('MMM D, YYYY')}
														</span>
													)}
												</p>
											</div>
											{isSelected && (
												<CheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 ml-2" />
											)}
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				{/* Footer */}
				<div className="flex justify-between items-center pt-4">
					<p className="text-sm text-gray-500">
						{selectedContracts.length > 0 && (
							<>
								{selectedContracts.length} contract{selectedContracts.length !== 1 ? 's' : ''} selected
								{options?.maxSelect && ` (max ${options.maxSelect})`}
							</>
						)}
					</p>
					<div className="flex gap-3">
						<Button type="secondary" onClick={setClose}>
							Cancel
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={selectedContracts.length === 0}
						>
							Add {selectedContracts.length > 0 && `(${selectedContracts.length})`}
						</Button>
					</div>
				</div>
			</div>
		</StackableModal>
	);
};

export default ContractPickerModal;
